import { TApiProjectSection } from "@timenotes/shared/src/services/api-client/types"
import { ColumnTitle } from "./styled-components/column-title"
import { ArchiveSvg, DeleteSvg, EditSVG, MoreSvg } from "assets/svg/icon"
import InPlaceEditContainer, { InPlaceEditContainerHandle } from "components/timenotes/in-place-edit-container"
import useUpdateProjectSectionMutation from "@timenotes/shared/src/services/project-sections/queries/use-update-project-section-mutation"
import { DownOutlined, LoadingOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons"
import { Button, message, Modal, Tooltip } from "antd"
import { plusButton } from "./styled-components/column"
import { GenericButtonsListWrapper } from "./styled-components/generic-buttons-list-wrapper"
import GenericButtonsList, { TGenericButton } from "components/generic-buttons-list/generic-buttons-list"
import { getApiErrorMessage } from "@timenotes/shared/src/services/api-client"
import { useRef } from "react"
import useDeleteProjectSectionMutation from "@timenotes/shared/src/services/project-sections/queries/use-delete-project-section-mutation"
import { useProjectBoard } from "@timenotes/shared/src/features/project-board"
import { ArchivedLabel } from "./archived-label"

interface ProjectSectionHeaderContainerProps extends Omit<ProjectSectionHeaderProps, 'onNameChange' | 'isLoading' | 'onArchive' | 'onUnarchive' | 'onDelete' | 'onAddTask'> {}

const ProjectSectionHeaderContainer = (props: ProjectSectionHeaderContainerProps) => {
  const {
    projectSection,
    style,
    isCollapsed,
    setIsCollapsed,
    displayArchived,
  } = props

  const {
    showNewTaskForm,
  } = useProjectBoard()

  const updateProjectSectionMutation = useUpdateProjectSectionMutation({
    onError: (error) => {
      message.error(getApiErrorMessage({ errors: error }, 'base'))
    }
  })

  const deleleteProjectSectionMutation = useDeleteProjectSectionMutation()

  const handleArchive: ProjectSectionHeaderProps['onArchive'] = () => {
    updateProjectSectionMutation.mutate({
      projectId: projectSection.projectId,
      projectSectionId: projectSection.id,
      status: 'archived',
    })
  }

  const handleUnarchive: ProjectSectionHeaderProps['onUnarchive'] = () => {
    updateProjectSectionMutation.mutate({
      projectId: projectSection.projectId,
      projectSectionId: projectSection.id,
      status: 'active',
    })
  }

  const handleNameChange: ProjectSectionHeaderProps['onNameChange'] = (newName) => {
    updateProjectSectionMutation.mutate({
      projectId: projectSection.projectId,
      projectSectionId: projectSection.id,
      name: newName,
    })
  }

  const handleDelete: ProjectSectionHeaderProps['onDelete'] = () => {
    deleleteProjectSectionMutation.mutate({
      projectId: projectSection.projectId,
      projectSectionId: projectSection.id
    })
  }

  const handleAddTask = () => {
    showNewTaskForm({
      projectSectionId: projectSection.id,
      projectId: projectSection.projectId,
      projectSectionPosition: 0,
    })
  }

  const isLoading = (updateProjectSectionMutation.isLoading && updateProjectSectionMutation.variables?.projectSectionId == projectSection.id) || (deleleteProjectSectionMutation.isLoading && deleleteProjectSectionMutation.variables?.projectSectionId == projectSection.id)

  return (
    <ProjectSectionHeader
      projectSection={projectSection}
      isLoading={isLoading}
      isCollapsed={isCollapsed}
      setIsCollapsed={setIsCollapsed}
      style={style}

      onNameChange={handleNameChange}
      onArchive={handleArchive}
      onUnarchive={handleUnarchive}
      onDelete={handleDelete}

      onAddTask={handleAddTask}
      displayArchived={displayArchived}
    />
  )

}

interface ProjectSectionHeaderProps {
  // Container shared props
  projectSection: TApiProjectSection
  isCollapsed?: boolean
  setIsCollapsed?(isCollapsed: boolean): void
  style?: React.CSSProperties
  displayArchived?: boolean

  // View only props
  isLoading: boolean
  onAddTask(): void
  onNameChange(name: string): void
  onArchive(): void
  onUnarchive(): void
  onDelete(): void
}

const ProjectSectionHeader = (props: ProjectSectionHeaderProps) => {
  const {
    projectSection,
    onNameChange,
    displayArchived,
    isLoading,
    isCollapsed,
    setIsCollapsed,
    style,
    onArchive,
    onUnarchive,
    onDelete,
    onAddTask,
  } = props

  const isCollapsible = isCollapsed !== undefined

  const isArchived = projectSection.status == "archived"

  const archiveActionTitle = !isArchived ? 'Archive Section' : "Unarchive Section"
  const statusCallback = isArchived ? onUnarchive : onArchive

  const inPlaceEditRef = useRef<InPlaceEditContainerHandle>(null)
  const triggerEdit = () => {
    inPlaceEditRef.current?.trigger()
  }

  const buttons: TGenericButton[] = [
    {
      title: "",
      icon: MoreSvg,
      type: "text",
      options: [
        {
          title: "Rename Section",
          Icon: EditSVG,
          callback: triggerEdit
        },
        {
          title: archiveActionTitle,
          Icon: ArchiveSvg,
          callback: statusCallback
        },
        {
          title: "Delete Section",
          Icon: DeleteSvg,
          callback: () => {
            Modal.confirm({
              onOk: onDelete,
              width: 500,
              okText: "Delete",
              okType: "primary",
              style: { whiteSpace: "pre-line" },
              okButtonProps: { danger: true },
              title: (
                <span style={{ whiteSpace: "nowrap" }}>
                  Are you sure you want to delete this section?
                </span>
              ),
              content:
                "It will delete all its tasks and corresponding time entries. This action can not be reversed!",
            })
          },
        },
      ],
    },
  ]

  return (
    <div style={{
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      ...style
    }}>

      {isCollapsible && (
        <div style={{
          width: '20px',
          flexShrink: 0,
          marginRight: '5px',
        }}>
          <Button
            type="text"
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={plusButton}
          >
            {isCollapsed ? <DownOutlined /> : <RightOutlined />}
          </Button>
        </div>
      )}

      <ColumnTitle
        style={{
          display: 'flex',
          height: '42px',
          flexGrow: 1, // B should take the remaining space
          flexShrink: 1, // Allow shrinking if parent is too small
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginTop: '10px',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <InPlaceEditContainer
          ref={inPlaceEditRef}
          value={projectSection.name}
          onChange={onNameChange}
          tooltip={`${projectSection.name} - click to rename the section`}
        >
          {projectSection.name}
          {isLoading && <LoadingOutlined />}
        </InPlaceEditContainer>

        {displayArchived && (
          <ArchivedLabel isArchived={projectSection.status == 'archived'} />
        )}
      </ColumnTitle>

      <div style={{
        display: 'flex',
        flexShrink: 0,
        width: '10px',
        marginLeft: '10px',
        marginRight: '20px',
      }}>
        <Tooltip title="Total tasks count in section">
          <span
            style={{
              fontWeight: "400",
              fontSize: "1.15rem",
              opacity: "0.5",
            }}
          >
            {projectSection.tasks.meta.pagination.totalCount}
          </span>
        </Tooltip>
      </div>

      <div style={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Add task on top of the section">
          <Button
            onClick={onAddTask}
            style={plusButton}
            type="text"
          >
            <PlusOutlined />
          </Button>
        </Tooltip>

        <Tooltip title="More Actions">
          <GenericButtonsListWrapper
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <GenericButtonsList buttons={buttons} />
          </GenericButtonsListWrapper>
        </Tooltip>
      </div>

    </div>
  )
}

export default ProjectSectionHeaderContainer